import React from 'react';

import API from '@/services';
import { USER_SEND_TYPE } from '@/types/history';
import { handleOrderRedirect } from '@/utils/btnClickHandler';
import {
  FOOD_BUYER_ORDER_LIST_TITLE_TEXT,
  FOOD_METHOD_MAP,
  FOOD_ORDER_TIME_TEXT,
  FOOD_SELECT_ORDER_TEXT,
  FOOD_TOTAL_TEXT,
  HISTORY_TEXT,
  INPUT_TYPE,
  LOAD_FOOD_LIST_API_KEY,
  LOAD_FOOD_LIST_CLIENT_KEY,
  ON_GOING_TEXT,
  OrderType as HistoryOrderType,
  PAY_METHOD_MAP,
  PAY_OTHER_METHOD_TEXT,
  TRACKING_MSG_TYPE,
  TRACKING_SECTION,
  TRACKING_TARGET,
  TRACKING_TYPE,
} from '@/utils/constants';
import { assignParams } from '@/utils/dataTypeConvert';
import { getTrans } from '@/utils/instantLoaded/tools';
import { jsonParse } from '@/utils/jsonParse';
import { formatPrice } from '@/utils/tools';
import dateLib from '@/utils/dateLib/moment';
import { OrderType } from '@shopee/chatbot-ui/lib/components/OrderListPopup';
import { formatTime } from '../FoodBuyerOrder';

interface LoadMoreParams {
  [name: string]: number;
  page_num: number;
  page_size: number;
  next_id: number;
}

interface FoofBuyerOrder {
  id: number;
  food_buyer_order_id: string;
  user_id: number;
  currency: string;
  place_time: number;
  store_id: number;
  store_logo: string;
  store_name: string;
  total_amount: number;
  payment_method: number;
  order_status: number;
  create_time: string;
  uri_key: string;
}

export enum FilterType {
  All,
  Ongoing,
  History,
}

const TABS = [
  { name: getTrans(ON_GOING_TEXT), type: FilterType.Ongoing },
  { name: getTrans(HISTORY_TEXT), type: FilterType.History },
];

const useForbidOrderRedirect = () => {
  const forbidOrderRedirectCountries = ['co', 'cl'];
  if (forbidOrderRedirectCountries.includes(COUNTRY)) {
    return true;
  }
  return false;
};

export const getPopupParams = (data: any) => {
  const {
    isHistory,
    nodePointId,
    taskbotName,
    historySessionId,
    sessionId,
    historyDialogueId,
    dialogueId,
    threadId,
    threadNodeId,
    threadIdV2,
    roundId,
    kbBotId,
    tfNodeId,
    tfCaseId,
  } = data.info || {};

  const generateOrder = (orderItem: FoofBuyerOrder) => {

    const formattedOrder = {
      totalPrice: `${formatPrice(orderItem.total_amount)}(${
        getTrans(PAY_METHOD_MAP[orderItem.payment_method as keyof typeof PAY_METHOD_MAP]) ||
        getTrans(PAY_OTHER_METHOD_TEXT)
      })`,
      shopName: orderItem.store_name,
      orderId: orderItem.food_buyer_order_id,
      title: orderItem.store_name,
      createdTime: formatTime(orderItem.place_time),
      image: orderItem.store_logo,
      specification: `${getTrans(FOOD_ORDER_TIME_TEXT)}: ${dateLib.format(orderItem.place_time, 'DD-MM-YYYY HH:ii')}`,
      totalText: getTrans(FOOD_TOTAL_TEXT),
      orderStatusText: getTrans(FOOD_METHOD_MAP[orderItem.order_status as keyof typeof FOOD_METHOD_MAP]),
      handleClick: useForbidOrderRedirect()
        ? undefined
        : () => handleOrderRedirect({ uri_key: orderItem.uri_key, order_id: orderItem.food_buyer_order_id }),
    };

    const nextParams = {
      session_id: sessionId,
      dialogue_id: dialogueId,
      thread_id: threadId,
      thread_node_id: threadNodeId,
      thread_id_v2: threadIdV2,
      round_id: roundId,
      input_type: INPUT_TYPE.BUTTON,
      is_bypass: data.is_bypass,
      message_text: orderItem.store_name,
      message_extra: {
        task_bot_action: {
          ...data.task_bot_action,
          params: assignParams(data.task_bot_action.params, { food_buyer_order_id: orderItem.food_buyer_order_id }),
          task_bot_info: data.taskbotInfo,
        },
        params: {
          // 历史消息
          user_send: {
            type: USER_SEND_TYPE.ORDER_SELECT,
            order_select: {
              ...orderItem,
              create_time: orderItem.place_time,
              order_type: HistoryOrderType.foodBuyer,
              total_price: orderItem.total_amount,
              name: orderItem.store_name,
              image: orderItem.store_logo,
              shouldRedirect: useForbidOrderRedirect(),
              id: orderItem?.food_buyer_order_id,
            },
          },
        },
      },
    };

    return {
      ...formattedOrder,
      selectAction: {
        // info为选择订单后回显的信息
        info: { ...formattedOrder, totalPrice: formatPrice(orderItem.total_amount) },
        func: 'showFoodBuyerOrder',
        nextParams,
      },
    };
  };

  const onLoadMore = async (loadMoreProps: LoadMoreParams) => {
    const params = {
      api_key: LOAD_FOOD_LIST_API_KEY,
      client_key: LOAD_FOOD_LIST_CLIENT_KEY,
    };

    const queryData = {
      last_id: loadMoreProps.next_id,
      page_size: loadMoreProps.page_size,
      filter_type: loadMoreProps.type,
    };

    const res = await (API as { getFoodBuyerOrderList: Function })
      .getFoodBuyerOrderList({ query_data: JSON.stringify(queryData), ...params }, { isErrorWhiteList: true })
      .catch(() => {
        throw new Error();
      });
    const resObj = jsonParse(res.orders);
    const orderList = resObj.orders?.map((item: FoofBuyerOrder) => generateOrder(item));

    return { data: orderList || [], nextId: resObj.next_id };
  };

  const trackingdataBasic = {
    chat_session_id: isHistory ? historySessionId : sessionId || '',
    chat_dialogue_id: isHistory ? historyDialogueId : dialogueId,
    node_point_id: nodePointId,
    if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
    task_bot_name: taskbotName,
    taskbot_id: data.info.taskbotInfo?.task_bot_id,
    taskflow_id: data.info.taskbotInfo?.flow_id,
    kb_bot_id: kbBotId,
    tf_node_id: tfNodeId,
    tf_case_id: tfCaseId,
  };

  const trackingProps = {
    componentTrackingData: {
      panelOrder: {
        trackingsection: TRACKING_SECTION[TRACKING_TYPE.TFE_FOOD_BUYER_ORDER_SELECTOR],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.TFE_FOOD_BUYER_ORDER_SELECTOR],
        trackingdataBasic,
        trackingItemData: (data: any) => ({
          location: data?.index,
          orderid: data?.orderId,
          order_status: data?.orderStatusText,
          tab_name: data?.tab?.name,
        }),
      },
      tab: {
        trackingsection: TRACKING_SECTION[TRACKING_TYPE.TFE_FOOD_BUYER_ORDER_LIST_TAB],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.TFE_FOOD_BUYER_ORDER_LIST_TAB],
        trackingdataBasic,
        trackingItemData: (data: any) => ({ tab_name: data?.title?.name }),
      },
      closePanel: {
        trackingsection: TRACKING_SECTION[TRACKING_TYPE.TFE_FOOD_BUYER_ORDER_LIST_CLOSE],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.TFE_FOOD_BUYER_ORDER_LIST_CLOSE],
        trackingdataBasic,
      },
      panelOrderScroll: {
        trackingsection: TRACKING_SECTION[TRACKING_TYPE.TFE_FOOD_BUYER_ORDER_LIST_SCROLL],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.TFE_FOOD_BUYER_ORDER_LIST_SCROLL],
        trackingdataBasic,
      },
      panelOrderList: {
        trackingsection: TRACKING_SECTION[TRACKING_TYPE.TFE_FOOD_BUYER_ORDER_LIST],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.TFE_FOOD_BUYER_ORDER_LIST],
        trackingdataBasic,
        trackingItemData: (data: any) => ({ tab_name: data?.tab?.name }),
      },
    },
  };

  return {
    limit: 20,
    onLoadMore,
    orderCardType: OrderType.buyerFood,
    panelTitleText: getTrans(FOOD_BUYER_ORDER_LIST_TITLE_TEXT),
    tabs: TABS,
    showTabs: true,
    active: true,
    selectOrderText: getTrans(FOOD_SELECT_ORDER_TEXT),
    ...trackingProps,
  };
};
